import { useMemo, useRef } from "react";
import { BaseAxisProps } from "recharts/types/util/types";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  ComponentRenderLocation,
  GraphConfig,
  GraphType,
  LineData,
} from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import { AudienceByMetricChart } from "@src/features/analytics/baseDashboard/charts/AudienceByMetricChart";
import { IgBarChart } from "@src/features/analytics/baseDashboard/charts/IgBarChart";
import { IgHistogramChart } from "@src/features/analytics/baseDashboard/charts/IgHistogramChart";
import { IgHorizStackedBarChart } from "@src/features/analytics/baseDashboard/charts/IgHorizStackedBarChart";
import { IgLineChart } from "@src/features/analytics/baseDashboard/charts/IgLineChart";
import { IgStackedAreaChart } from "@src/features/analytics/baseDashboard/charts/IgStackedAreaChart";
import { IgStackedBarChart } from "@src/features/analytics/baseDashboard/charts/IgStackedBarChart";
import { NoGraphData } from "@src/features/analytics/baseDashboard/charts/NoGraphData";
import { OrderValueChart } from "@src/features/analytics/baseDashboard/charts/OrderValueChart";
import { ProfitAndRevenueChart } from "@src/features/analytics/baseDashboard/charts/ProfitAndRevenueChart";
import { SankeyGraph } from "@src/features/analytics/baseDashboard/charts/SankeyGraph";
import { UnitQuantityByMetricChart } from "@src/features/analytics/baseDashboard/charts/UnitQuantityByMetricChart";
import { ChartSkeletonWrapper } from "@src/features/analytics/baseDashboard/ChartWrapperSkeleton";
import {
  SitewideAnalyticsAudienceQueryResult,
  SitewideAnalyticsHolidayBenchmarkingQueryResult,
  SitewideAnalyticsPerformanceQueryResult,
} from "@src/features/analytics/types";
import { hasFiltersBeenModified } from "@src/features/analytics/utils";
import { useSelector } from "@src/store";
import {
  BarMetricData,
  GraphMetricData,
  HistogramMetricData,
  LineMetricData,
  SankeyMetricData,
  StackedAreaMetricData,
  StackedBarMetricData,
} from "@src/types/analytics";
import { UseQueryResult } from "@tanstack/react-query";

import { useAddGraphWatermark } from "../../hooks/use-add-graph-watermark";

interface GraphRenderProps {
  id?: string;
  config: GraphConfig;
  renderLocation: ComponentRenderLocation;
  data: GraphMetricData | undefined;
  filterMethod?: string;
  isSharing?: boolean;
  focusedVariationId?: string;
  syncId?: string;
  focusGroups?: string[];
  margin?: { top?: number; bottom?: number; right?: number; left?: number };
  totalTestVisitors: number;
  testDuration: number;
  onMetricDetailsClick?: () => void;
  onAddKeyMetricClick?: () => void;
  isDataLoading: boolean;
  compareVariationId?: string;
  setBarCellColor?: (params: {
    compareVariationId?: string;
    data: BarMetricData;
    index: number;
  }) => string;
  setStackedBarCellColor?: (params: {
    compareVariationId?: string;
    data: StackedBarMetricData;
    index: number;
  }) => string;
  rawData?:
    | UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[]
    | SitewideAnalyticsAudienceQueryResult
    | SitewideAnalyticsPerformanceQueryResult
    | SitewideAnalyticsHolidayBenchmarkingQueryResult;
  lines: LineData[] | undefined;
  showWatermark?: boolean;
  watermarkPosition?: "left" | "right";
  xAxisProps?: BaseAxisProps;
  yAxisProps?: BaseAxisProps;
  intervalOfTheData?: number;
  dateFormat?: string;
  graphHeight?: number;
}
export const ChartWrapper = ({
  id,
  config,
  renderLocation,
  data,
  filterMethod,
  isSharing,
  focusedVariationId,
  syncId,
  focusGroups,
  margin,
  totalTestVisitors,
  onMetricDetailsClick,
  onAddKeyMetricClick,
  testDuration,
  isDataLoading,
  compareVariationId,
  setBarCellColor,
  setStackedBarCellColor,
  rawData,
  lines,
  showWatermark,
  watermarkPosition,
  xAxisProps,
  yAxisProps,
  dateFormat,
  graphHeight,
}: GraphRenderProps) => {
  const dashboardFilters = useSelector((state) => state.dashboardFilters);
  const gridRef = useRef<HTMLDivElement>(null);
  useAddGraphWatermark({
    containerRef: gridRef,
    isDataLoading,
    showWatermark,
    position: watermarkPosition,
  });

  const areFiltersModified = useMemo(() => {
    return hasFiltersBeenModified(dashboardFilters);
  }, [dashboardFilters]);

  return (
    <Grid id={id} ref={gridRef} sx={{ width: "100%" }}>
      {!isDataLoading && (!data || data.length < 1 || data.length === 0) ? (
        <NoGraphData
          title={areFiltersModified ? "No Data!" : undefined}
          body={
            areFiltersModified
              ? "Your filters may have resulted in no data. Try resetting them!"
              : ""
          }
        />
      ) : isDataLoading ? (
        <ChartSkeletonWrapper config={config} renderLocation={renderLocation} />
      ) : data ? (
        <Grid>
          {config.graphType === GraphType.StackedBar ? (
            <IgStackedBarChart
              config={config}
              groupMetricData={data as StackedBarMetricData[]}
              onAddKeyMetricClick={onAddKeyMetricClick}
              onMetricDetailsClick={onMetricDetailsClick}
              compareVariationId={compareVariationId}
              testDuration={testDuration}
              graphHeight={graphHeight}
              setCellColor={setStackedBarCellColor}
            />
          ) : config.graphType === GraphType.Histogram ? (
            <IgHistogramChart
              config={config}
              groupMetricData={data as HistogramMetricData[]}
              selectedVariationId={focusedVariationId}
              graphHeight={graphHeight}
            />
          ) : config.graphType === GraphType.StackedBarHorizontal ? (
            <IgHorizStackedBarChart
              config={config}
              groupMetricData={data as StackedBarMetricData[]}
              graphHeight={graphHeight}
            />
          ) : config.graphType === GraphType.StackedArea ? (
            <IgStackedAreaChart
              config={config}
              groupMetricData={data as StackedAreaMetricData[]}
              graphHeight={graphHeight}
            />
          ) : config.graphType === GraphType.Line && lines ? (
            <IgLineChart
              config={config}
              groupMetricData={data as LineMetricData[]}
              externalFocusGroups={focusGroups}
              syncId={syncId}
              lines={lines}
              xAxisProps={xAxisProps}
              yAxisProps={yAxisProps}
              dateFormat={dateFormat}
              graphHeight={graphHeight}
            />
          ) : config.graphType === GraphType.ProfitAndRevenue && lines ? (
            <ProfitAndRevenueChart
              config={config}
              groupMetricData={data as LineMetricData[]}
              externalFocusGroups={focusGroups}
              syncId={syncId}
              legends={lines}
              dateFormat={dateFormat}
              graphHeight={graphHeight}
            />
          ) : config.graphType === GraphType.AudienceByMetric && lines ? (
            <AudienceByMetricChart
              config={config}
              filterMethod={filterMethod}
              groupMetricData={data as LineMetricData[]}
              legends={lines}
              syncId={syncId}
              dateFormat={dateFormat}
              graphHeight={graphHeight}
            />
          ) : config.graphType === GraphType.UnitQuantityByMetric && lines ? (
            <UnitQuantityByMetricChart
              config={config}
              groupMetricData={data as LineMetricData[]}
              filterMethod={filterMethod}
              legends={lines}
              syncId={syncId}
              dateFormat={dateFormat}
              graphHeight={graphHeight}
            />
          ) : config.graphType === GraphType.Sankey ? (
            <SankeyGraph
              {...((data as SankeyMetricData[]).find(
                (d) => d.variation_id === focusedVariationId
              ) ?? { nodes: [], links: [], nVisitors: 0 })}
              height={graphHeight}
            />
          ) : config.graphType === GraphType.OrderValue ? (
            <OrderValueChart
              config={config}
              groupMetricData={data as BarMetricData[]}
              filterMethod={filterMethod}
              focusGroups={focusGroups}
              legends={lines}
              syncId={syncId}
              dateFormat={dateFormat}
              graphHeight={graphHeight}
            />
          ) : (
            rawData && (
              <IgBarChart
                config={config}
                groupMetricData={data as BarMetricData[]}
                filterMethod={filterMethod}
                isSharing={isSharing}
                syncId={syncId}
                focusGroups={focusGroups}
                margin={margin}
                totalTestVisitors={totalTestVisitors}
                testDuration={testDuration}
                compareVariationId={compareVariationId}
                setCellColor={setBarCellColor}
                rawData={rawData}
                graphHeight={graphHeight}
              />
            )
          )}
        </Grid>
      ) : null}
    </Grid>
  );
};
